import { type SelectChangeEvent, FormControl, Select, MenuItem } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';

import useTranslation from 'canvas_states/translation/useTranslation';
import type Language from '../../../../types/Language';
import type Currency from '../../../../types/payment/Currency';
import FormLabel from '../../atoms/FormLabel';
import useCommmonEvents from '../../utils/analytics/useCommonEvents';
import { GoogleAnalyticClick } from '../../../../types/analytics/GoogleAnalyticsEvent';

export const dataTestId = {
  currencySelector: 'currencySelector',
  languageSelector: 'languageSelector',
  languageSelectorItem: (code: string) => `languageSelector.item.${code}`,
};

export interface LanguageCurrencySelectorsProps {
  handleLanguageChange: (event: SelectChangeEvent) => void;
  handleCurrencyChange: (event: SelectChangeEvent) => void;
  hideCurrencySelector?: boolean; // if the user already has items in the card it can't change the currency anymore
  disableCurrencySelector?: boolean;
  hideLanguagesSelector?: boolean;
  languages?: Language[];
  currencies?: Currency[];
  currentCurrency?: Currency;
  currentLanguage:
    | Language
    | {
        code: string;
        label: '';
      };
}

const LanguageCurrencySelectors: React.FC<LanguageCurrencySelectorsProps> = ({
  handleLanguageChange,
  handleCurrencyChange,
  hideCurrencySelector,
  disableCurrencySelector,
  hideLanguagesSelector,
  languages = [],
  currencies = [],
  currentCurrency = { code: '', label: '', symbol: '' },
  currentLanguage,
}) => {
  const { t } = useTranslation().actions;
  const eventPublisher = useCommmonEvents();

  const handleLanguageSelectOpen = () => {
    eventPublisher.actions.publishClick(GoogleAnalyticClick.CHANGE_LANGUAGE_DROPDOWN);
  };

  return (
    <Grid container direction="column">
      {!hideLanguagesSelector && (
        <FormControl fullWidth variant="filled" margin="dense">
          <FormLabel id="language-select">{t('header.language')}</FormLabel>
          <Select
            labelId="language-select"
            value={currentLanguage?.code ?? ''}
            onChange={handleLanguageChange}
            onOpen={handleLanguageSelectOpen}
            inputProps={{ 'data-testid': dataTestId.languageSelector }}
          >
            {languages.map(({ code }) => (
              <MenuItem key={code} value={code} data-testid={dataTestId.languageSelectorItem(code)}>
                {t(`common.language.${code}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {!hideCurrencySelector && (
        <FormControl fullWidth variant="filled" margin="dense">
          <FormLabel id="currency-select">{t('header.currency')}</FormLabel>
          <Select
            labelId="currency-select"
            value={currentCurrency?.code ?? ''}
            onChange={handleCurrencyChange}
            inputProps={{ 'data-testid': dataTestId.currencySelector }}
            disabled={disableCurrencySelector}
          >
            {currencies.map(({ code }) => (
              <MenuItem key={code} value={code}>
                {t(`common.currency.${code}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Grid>
  );
};

export default LanguageCurrencySelectors;
